import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasManualSpacing, readDocument } from "@/office/word/document";

export const lektionEinzuege: Lesson = {
  id: "einzuege",
  title: "Einzüge statt Leerzeichen",
  summary: "Text einrücken, ohne die Leertaste zu missbrauchen.",
  durationMinutes: 8,
  learningGoals: [
    "Linken Einzug und Erstzeileneinzug unterscheiden",
    "Einzüge über das Lineal und den Absatzdialog setzen",
    "Erkennen, warum Leerzeichen und Tabulatoren als Einzug scheitern",
  ],
  steps: [
    {
      id: "linker-einzug",
      title: "Den linken Einzug setzen",
      explanation: [
        "Dasselbe Muster wie bei den Leerzeilen, nur waagerecht: Um Text einzurücken, " +
          "drücken viele mehrmals die Leertaste oder Tab. Das Ergebnis hält genau so " +
          "lange, bis sich die Schriftgröße oder der Text ändert – dann steht die zweite " +
          "Zeile plötzlich woanders als die erste.",
        "Der linke Einzug rückt den kompletten Absatz ein, jede Zeile gleich weit. Er " +
          "wird in Zentimetern gemessen und gilt für den ganzen Absatz, egal wie lang er " +
          "wird.",
        "Am schnellsten geht es über die beiden Schaltflächen „Einzug vergrößern“ und " +
          "„Einzug verkleinern“ auf der Registerkarte „Start“ – oder über das Lineal, das " +
          "du unter „Ansicht“ einblenden kannst.",
      ],
      keyPoints: [
        "Linker Einzug rückt alle Zeilen des Absatzes gleich weit ein.",
        "Leerzeichen und Tabulatoren als Einzug verrutschen bei jeder Änderung.",
        "Ansicht → Lineal blendet die Einzugsmarken ein.",
      ],
      task: {
        instruction:
          "Rücke einen deiner Absätze mit dem linken Einzug ein – und verwende dafür " +
          "keine Leerzeichen oder Tabulatoren.",
        hint: "Cursor in den Absatz, dann auf der Registerkarte „Start“ auf „Einzug vergrößern“ klicken.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.filledParagraphs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst etwas Text.");
          }

          const eingerueckt = snapshot.filledParagraphs.find(
            (absatz) => typeof absatz.format.leftIndent === "number" && absatz.format.leftIndent > 0,
          );
          const nachgeahmt = snapshot.filledParagraphs.filter(hasManualSpacing);

          return fromCriteria(
            [
              criterion("Ein Absatz hat einen linken Einzug", Boolean(eingerueckt)),
              criterion(
                `Kein Absatz nutzt Tabulatoren oder mehrere Leerzeichen als Einzug (gefunden: ${nachgeahmt.length})`,
                nachgeahmt.length === 0,
              ),
            ],
            {
              success: "Sehr gut – der Einzug bleibt jetzt stabil, egal wie sich der Text ändert.",
              failure: "Da ist noch etwas offen.",
              hint: "Blende mit Strg + Umschalt + 8 die Steuerzeichen ein. Tabulatoren erscheinen dann als kleine Pfeile.",
            },
          );
        },
      },
    },
    {
      id: "erstzeileneinzug",
      title: "Erstzeileneinzug und hängender Einzug",
      explanation: [
        "Zwei Sonderformen sind gut zu kennen. Der Erstzeileneinzug rückt nur die erste " +
          "Zeile eines Absatzes ein – die klassische Variante, um Absätze in einem " +
          "Fließtext ohne Leerraum voneinander abzusetzen, wie man es aus Büchern kennt.",
        "Der hängende Einzug macht das Gegenteil: Die erste Zeile bleibt stehen, alle " +
          "folgenden werden eingerückt. Das ist das Format für Literaturverzeichnisse und " +
          "Glossare – und genau das, was Word bei Aufzählungen automatisch verwendet, " +
          "damit der Text sauber unter dem Text und nicht unter dem Aufzählungszeichen " +
          "weiterläuft.",
        "Beides stellst du im Absatzdialog unter „Sondereinzug“ ein. Entweder du " +
          "entscheidest dich für Erstzeileneinzug oder für Absatzabstand – beides " +
          "zusammen ist zu viel des Guten.",
      ],
      keyPoints: [
        "Erstzeileneinzug: nur die erste Zeile rückt ein.",
        "Hängender Einzug: alle Zeilen außer der ersten rücken ein.",
        "Entweder Erstzeileneinzug oder Absatzabstand – nicht beides.",
      ],
      task: {
        instruction:
          "Gib einem Absatz einen Erstzeileneinzug. Achte darauf, dass derselbe Absatz " +
          "dann keinen zusätzlichen Absatzabstand mehr hat.",
        hint: "Absatzdialog öffnen (Start → kleiner Pfeil bei „Absatz“) → Sondereinzug → Erste Zeile.",
        check: async () => {
          const snapshot = await readDocument();
          const mitEinzug = snapshot.filledParagraphs.find(
            (absatz) =>
              typeof absatz.format.firstLineIndent === "number" &&
              absatz.format.firstLineIndent > 0,
          );

          if (!mitEinzug) {
            return fail(
              "Noch kein Absatz mit Erstzeileneinzug gefunden.",
              "Der hängende Einzug hat einen negativen Wert – hier ist der positive Erstzeileneinzug gemeint.",
            );
          }

          return fromCriteria(
            [
              criterion("Ein Absatz hat einen Erstzeileneinzug", true),
              criterion(
                "Derselbe Absatz hat keinen zusätzlichen Absatzabstand",
                (mitEinzug.format.spaceAfter ?? 0) === 0,
              ),
            ],
            {
              success: "Genau richtig – eine Trennung pro Absatz genügt.",
              failure: "Der Absatz hat Erstzeileneinzug und Absatzabstand gleichzeitig.",
              hint: "Setze für diesen Absatz den Abstand „Nach“ auf 0 pt.",
            },
          );
        },
      },
    },
  ],
};
